import * as z from "zod";

import { INonGenericDevice, ISchedule } from "interfaces/device.interface";

const schedule = z
  .object({
    enabled: z.boolean(),
    enable_session: z.boolean(),
    timespan: z.object({
      start: z.string(),
      end: z.string(),
    }),
  })
  .array();

const radar = z.object({
  distance_limit: z.number(),
});

const time = z.object({
  format: z.enum(["12", "24"]),
  timezone: z.string(),
});

const sm100Schema = z.object({
  type: z.literal("vitalthings-somnofy-sm100"),
  name: z.string(),
  serial_number: z.string(),
  subject_id: z.string().nullable(),
  zone_id: z.string().nullable(),
  settings: z.object({
    user: z.object({
      display: z.object({
        mode: z.enum(["normal", "circle", "off"]),
        intensity: z.number(),
      }),
      audio: z.object({
        enabled: z.boolean(),
      }),
      time,
      radar,
      schedule,
    }),
  }),
});

const m10Schema = z.object({
  type: z.literal("vitalthings-guardian-h10"),
  name: z.string(),
  serial_number: z.string(),
  subject_id: z.string().nullable(),
  zone_id: z.string().nullable(),
  settings: z.object({
    user: z.object({
      time,
      radar,
      schedule,
    }),
  }),
});

export const deviceFormSchema = z.discriminatedUnion("type", [
  sm100Schema,
  m10Schema,
]);

export type DeviceFormSchema = z.infer<typeof deviceFormSchema>;

const setDefaultSchedule = (
  schedule: ISchedule | undefined | null,
): DeviceFormSchema["settings"]["user"]["schedule"] => {
  return [
    {
      enabled: schedule?.enabled ?? false,
      enable_session: schedule?.enable_session ?? false,
      timespan: {
        start: schedule?.timespan?.start ?? "00:00",
        end: schedule?.timespan?.end ?? "00:00",
      },
    },
  ];
};

export const setDefaults = (
  deviceData: INonGenericDevice,
): DeviceFormSchema => {
  const schedule = deviceData.settings?.user?.schedule?.[0];

  if (deviceData.type === "vitalthings-somnofy-sm100") {
    return {
      type: deviceData.type,
      name: deviceData.name ?? "",
      serial_number: deviceData.serial_number ?? "",
      subject_id: deviceData.subject_id ?? null,
      zone_id: deviceData.zone_id ?? null,
      settings: {
        user: {
          display: {
            mode: deviceData.settings?.user?.display?.mode ?? "normal",
            intensity: deviceData.settings?.user?.display?.intensity ?? 100,
          },
          audio: {
            enabled: deviceData.settings?.user?.audio?.enabled ?? true,
          },
          radar: {
            distance_limit:
              deviceData.settings?.user?.radar?.distance_limit ?? 3,
          },
          time: {
            format: deviceData.settings?.user?.time?.format ?? "24",
            timezone: deviceData.settings?.user?.time?.timezone ?? "",
          },
          schedule: setDefaultSchedule(schedule),
        },
      },
    } as const;
  }

  return {
    type: deviceData.type,
    name: deviceData.name ?? "",
    serial_number: deviceData.serial_number ?? "",
    subject_id: deviceData.subject_id ?? null,
    zone_id: deviceData.zone_id ?? null,
    settings: {
      user: {
        radar: {
          distance_limit: deviceData.settings?.user?.radar?.distance_limit ?? 3,
        },
        time: {
          format: deviceData.settings?.user?.time?.format ?? "24",
          timezone: deviceData.settings?.user?.time?.timezone ?? "",
        },
        schedule: setDefaultSchedule(schedule),
      },
    },
  } as const;
};
